/**
 * ESCÁNER (F13.3) — un solo componente para pistola, cámara y teclado
 *
 * Las tres entradas acaban en el MISMO resolvedor del servidor y entregan a la
 * pantalla una `Lectura` (artículo, lote, serie…). La pantalla decide qué hace
 * con ella (sumar a una línea, rellenar un formulario) y puede rechazarla con
 * un texto (p. ej. «serie repetida»). Escanear nunca mueve stock: sólo prepara
 * un borrador; el stock cambia al confirmar.
 *
 *   PISTOLA  campo siempre listo (foco), Enter procesa y NO envía el formulario,
 *            lecturas seguidas en cola, doble lectura accidental (mismo valor
 *            < 300 ms) ignorada.
 *   CÁMARA   trasera por defecto, cambiar cámara, linterna si existe, pausa tras
 *            cada lectura válida, cierre limpio del stream.
 *   MANUAL   el mismo campo con botón, para cuando no hay pistola ni cámara.
 * Ambiguo: se eligen entre candidatos (nunca solo). Lote leído que no existe:
 * «Crear lote» sin salir del flujo, sólo si el servidor dice que se puede.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { api, FalloApi } from '../api.ts';
import { avisar, type TipoAviso } from '../escaner/aviso.ts';
import { abrirCamara, camaraDisponible, cerrarStream, clasificarError, crearMotor, linterna, listarCamaras, muestrear, tieneLinterna, type ErrorCamara } from '../escaner/camara.ts';
import { textoRecurso, type AlLeer, type ContextoEscaneo, type Lectura, type RecursoEscaneo, type ResultadoEscaneo } from '../escaner/tipos.ts';

export type ModoEscaner = 'PISTOLA' | 'CAMARA' | 'MANUAL';
/** Doble lectura accidental: el mismo valor dentro de esta ventana se ignora. */
export const VENTANA_DOBLE_LECTURA_MS = 300;

const TEXTO_ERROR_CAMARA: Record<ErrorCamara, string> = {
  SIN_SOPORTE: 'Este navegador no permite usar la cámara. Usa la pistola o escribe el código.',
  DENEGADA: 'No hay permiso para usar la cámara. Concédelo en los ajustes del navegador o escribe el código.',
  SIN_CAMARA: 'No se encuentra ninguna cámara en este dispositivo.',
  OCUPADA: 'La cámara está ocupada por otra aplicación.',
  MOTOR: 'El lector de la cámara no ha podido arrancar en este navegador. Usa la pistola o escribe el código.',
  OTRO: 'No se ha podido abrir la cámara.',
};

function modoPorDefecto(): ModoEscaner {
  const tactil = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  return tactil && camaraDisponible() ? 'CAMARA' : 'PISTOLA';
}

interface Estado { tipo: TipoAviso; texto: string; detalle?: string[]; formato?: string | null }

export function Escaner({ contexto, alLeer, titulo = 'Escanear', modoInicial, deshabilitado, textoExito = 'Añadido: ' }: {
  contexto: ContextoEscaneo; alLeer: AlLeer; titulo?: string; modoInicial?: ModoEscaner;
  /** F13.4: qué significa una lectura válida en esta pantalla («Añadido: », «Localizado: »…). */
  textoExito?: string;
  /** Si hay texto, el escáner no lee y lo explica (p. ej. «Elige antes el almacén»). */
  deshabilitado?: string | null;
}): ReactNode {
  const [modo, setModo] = useState<ModoEscaner>(() => modoInicial ?? modoPorDefecto());
  const [valor, setValor] = useState('');
  const [estado, setEstado] = useState<Estado | null>(null);
  const [candidatos, setCandidatos] = useState<{ r: ResultadoEscaneo; lista: RecursoEscaneo[] } | null>(null);
  const [loteNuevo, setLoteNuevo] = useState<ResultadoEscaneo | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const campo = useRef<HTMLInputElement | null>(null);
  const ultimo = useRef<{ v: string; t: number } | null>(null);
  const cola = useRef<Promise<void>>(Promise.resolve());
  const ctxRef = useRef(contexto);
  ctxRef.current = contexto;
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;

  const avisarEstado = (e: Estado): void => { setEstado(e); avisar(e.tipo); };
  const enfocar = useCallback(() => { if (modo === 'PISTOLA') campo.current?.focus(); }, [modo]);

  async function entregar(r: ResultadoEscaneo, recurso: RecursoEscaneo): Promise<void> {
    const l: Lectura = { recurso, cantidad: r.cantidad, seriePendiente: r.seriePendiente, valor: r.valor };
    const error = await alLeerRef.current(l);
    if (error !== null) avisarEstado({ tipo: 'error', texto: error });
    else avisarEstado({ tipo: 'exito', texto: textoRecurso(recurso), detalle: r.avisos.length > 0 ? r.avisos : undefined, formato: r.formato ?? null });
  }

  async function procesar(texto: string, simbologia?: string, formato?: string): Promise<void> {
    const v = texto.replace(/^[\r\n\t ]+|[\r\n\t ]+$/g, '');
    if (v === '') return;
    const ahora = Date.now();
    if (ultimo.current !== null && ultimo.current.v === v && ahora - ultimo.current.t < VENTANA_DOBLE_LECTURA_MS) return;
    ultimo.current = { v, t: ahora };
    setCandidatos(null);
    setLoteNuevo(null);
    setOcupado(true);
    try {
      const c = ctxRef.current;
      const r = await api.post<ResultadoEscaneo>('/api/inventario/escaneo', {
        valor: v, ...(simbologia ? { simbologia } : {}), ...(formato ? { tipoDetectado: formato } : {}),
        contexto: { uso: c.uso, ...(c.idAlmacen ? { idAlmacen: c.idAlmacen } : {}), ...(c.idOperacion ? { idOperacion: c.idOperacion } : {}), ...(c.idArticulo ? { idArticulo: c.idArticulo } : {}) },
      });
      if (r.resultado === 'AMBIGUO') {
        setCandidatos({ r, lista: r.candidatos });
        avisarEstado({ tipo: 'ambiguo', texto: 'Este código identifica varias cosas: elige cuál es.' });
      } else if (r.resultado === 'DESCONOCIDO') {
        avisarEstado({ tipo: 'desconocido', texto: r.avisos[0] ?? 'Código desconocido.', detalle: r.pendiente !== null ? ['Queda anotado en la bandeja de pendientes para administración.'] : undefined });
      } else if (r.lotePendiente !== null && r.recurso?.tipo === 'ARTICULO' && r.recurso.articulo?.controlaLotes === true) {
        if (r.puedeCrearLote) {
          setLoteNuevo(r);
          avisarEstado({ tipo: 'ambiguo', texto: `El lote ${r.lotePendiente.codigo} de ${r.recurso.articulo.codigoNegocio} no existe todavía.` });
        } else {
          avisarEstado({ tipo: 'desconocido', texto: `El lote ${r.lotePendiente.codigo} de ${r.recurso.articulo.codigoNegocio} no existe y aquí no se puede dar de alta.` });
        }
      } else if (r.recurso !== null) {
        await entregar(r, r.recurso);
      }
    } catch (e) {
      avisarEstado({ tipo: 'error', texto: e instanceof FalloApi ? e.message : 'No se ha podido resolver el código.' });
    } finally {
      setOcupado(false);
      enfocar();
    }
  }
  /** Lecturas rápidas seguidas: en cola, en orden, sin perder ninguna. */
  const encolar = (texto: string, simbologia?: string, formato?: string): void => {
    cola.current = cola.current.then(() => procesar(texto, simbologia, formato));
  };

  async function crearLote(): Promise<void> {
    const r = loteNuevo;
    if (r === null || r.recurso?.articulo == null || r.lotePendiente === null) return;
    setOcupado(true);
    try {
      const l = await api.post<{ id: string; codigo: string; fechaCaducidad: string | null; estado?: 'ACTIVO' | 'BLOQUEADO'; activo: boolean }>('/api/inventario/lotes', {
        idArticulo: r.recurso.articulo.id, codigo: r.lotePendiente.codigo,
        fechaCaducidad: r.lotePendiente.fechaCaducidad, fechaFabricacion: r.lotePendiente.fechaFabricacion,
      });
      setLoteNuevo(null);
      await entregar(r, { ...r.recurso, tipo: 'LOTE', lote: { id: l.id, codigo: l.codigo, fechaCaducidad: l.fechaCaducidad, estado: l.estado ?? 'ACTIVO', activo: l.activo } });
    } catch (e) {
      avisarEstado({ tipo: 'error', texto: e instanceof FalloApi ? e.message : 'No se ha podido crear el lote.' });
    } finally {
      setOcupado(false);
      enfocar();
    }
  }

  useEffect(() => { if (deshabilitado == null) enfocar(); }, [enfocar, deshabilitado]);

  const clase = estado === null ? '' : estado.tipo === 'exito' ? 'exito' : estado.tipo === 'error' ? 'error' : 'aviso';
  return (
    <section className="panel escaner" aria-label={titulo} style={{ padding: 12 }}>
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <strong>{titulo}</strong>
        <div className="fila" role="group" aria-label="Modo de escaneo" style={{ gap: 6, flexWrap: 'wrap' }}>
          {(['PISTOLA', 'CAMARA', 'MANUAL'] as const).map((m) => (
            <button key={m} type="button" aria-pressed={modo === m} className={modo === m ? 'principal' : undefined}
              disabled={m === 'CAMARA' && !camaraDisponible()} onClick={() => setModo(m)}>
              {m === 'PISTOLA' ? 'Pistola' : m === 'CAMARA' ? 'Cámara' : 'Teclado'}
            </button>
          ))}
        </div>
      </div>
      {deshabilitado != null ? <p className="suave pequena" style={{ marginTop: 8 }}>{deshabilitado}</p> : (
        <>
          {modo !== 'CAMARA' && (
            <div className="fila" style={{ gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
              <div className="campo" style={{ flex: 1, margin: 0 }}>
                <label htmlFor="escaner-valor">{modo === 'PISTOLA' ? 'Lee con la pistola (el campo está listo)' : 'Escribe el código y pulsa Intro'}</label>
                <input id="escaner-valor" ref={campo} autoComplete="off" autoCapitalize="off" spellCheck={false} enterKeyHint="search"
                  value={valor} onChange={(ev) => setValor(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key !== 'Enter') return;
                    //  Enter de la pistola: se procesa aquí y NUNCA envía el formulario que contiene al escáner.
                    ev.preventDefault();
                    ev.stopPropagation();
                    const v = valor;
                    setValor('');
                    encolar(v);
                  }} />
              </div>
              {modo === 'MANUAL' && <button type="button" disabled={valor.trim() === '' || ocupado} onClick={() => { const v = valor; setValor(''); encolar(v); }}>Buscar</button>}
            </div>
          )}
          {modo === 'CAMARA' && <Camara alLeer={(t, s, f) => encolar(t, s, f)} alFallar={() => setModo('MANUAL')} ocupado={ocupado} />}
        </>
      )}
      <div role="status" aria-live="polite" className={estado === null ? 'suave pequena' : `nota ${clase}`} style={{ marginTop: 8 }}>
        {ocupado ? 'Resolviendo…' : estado === null ? 'Nada escaneado todavía.' : (
          <>
            <strong>{estado.tipo === 'exito' ? textoExito : estado.tipo === 'error' ? (textoExito === 'Añadido: ' ? 'No se ha añadido: ' : 'No encontrado: ') : ''}</strong>{estado.texto}
            {estado.detalle?.map((d) => <div key={d} className="pequena">{d}</div>)}
            {estado.formato != null && <div className="pequena suave">Código leído mediante: {estado.formato}</div>}
          </>
        )}
      </div>
      {candidatos !== null && (
        <div role="group" aria-label="Elige el que corresponde" className="fila" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, marginTop: 8 }}>
          {candidatos.lista.map((c, i) => (
            <button key={i} type="button" style={{ minHeight: 48, textAlign: 'left' }}
              onClick={() => { const r = candidatos.r; setCandidatos(null); void entregar(r, c).finally(enfocar); }}>{textoRecurso(c)}</button>
          ))}
          <button type="button" onClick={() => { setCandidatos(null); enfocar(); }}>Ninguno</button>
        </div>
      )}
      {loteNuevo !== null && (
        <div className="fila" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" className="principal" style={{ minHeight: 48 }} disabled={ocupado} onClick={() => void crearLote()}>
            Crear lote {loteNuevo.lotePendiente?.codigo}{loteNuevo.lotePendiente?.fechaCaducidad ? ` (cad. ${loteNuevo.lotePendiente.fechaCaducidad})` : ''}
          </button>
          <button type="button" onClick={() => { setLoteNuevo(null); enfocar(); }}>Cancelar</button>
        </div>
      )}
    </section>
  );
}

/** Vista de cámara: pide permiso al abrirse, lee hasta la primera lectura válida y se pausa. */
function Camara({ alLeer, alFallar, ocupado }: { alLeer: (texto: string, simbologia: string, formato: string) => void; alFallar: () => void; ocupado: boolean }): ReactNode {
  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const motor = useRef<Worker | null>(null);
  const parar = useRef<(() => void) | null>(null);
  const [error, setError] = useState<ErrorCamara | null>(camaraDisponible() ? null : 'SIN_SOPORTE');
  const [camaras, setCamaras] = useState<{ id: string; nombre: string }[]>([]);
  const [idCamara, setIdCamara] = useState<string | null>(null);
  const [pausada, setPausada] = useState(false);
  const [conLinterna, setConLinterna] = useState(false);
  const [luz, setLuz] = useState(false);

  //  alLeer va por ref: si cambiase con cada render del padre, el efecto reabriría la cámara.
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;
  const leer = useCallback(() => {
    if (video.current === null || motor.current === null) return;
    parar.current?.();
    setPausada(false);
    parar.current = muestrear(video.current, motor.current, (l) => { setPausada(true); alLeerRef.current(l.texto, l.simbologia, l.formato); }, () => {
      cerrarStream(stream.current);
      stream.current = null;
      setError('MOTOR');
    });
  }, []);

  useEffect(() => {
    if (!camaraDisponible()) return undefined;
    let cancelado = false;
    void (async () => {
      try {
        const s = await abrirCamara(idCamara);
        if (cancelado) { cerrarStream(s); return; }
        stream.current = s;
        if (video.current !== null) {
          video.current.srcObject = s;
          try { await video.current.play(); } catch { /* autoplay: el vídeo arranca al pulsar */ }
        }
        motor.current ??= crearMotor();
        setConLinterna(tieneLinterna(s));
        setCamaras(await listarCamaras());
        setError(null);
        leer();
      } catch (e) {
        if (!cancelado) setError(clasificarError(e));
      }
    })();
    return () => {
      cancelado = true;
      parar.current?.();
      parar.current = null;
      cerrarStream(stream.current);
      stream.current = null;
    };
  }, [idCamara, leer]);
  //  Al salir del modo cámara, el motor también se cierra.
  useEffect(() => () => { motor.current?.terminate(); motor.current = null; }, []);

  if (error !== null) {
    return (
      <div className="nota error" role="alert" style={{ marginTop: 8 }}>
        {TEXTO_ERROR_CAMARA[error]} <button type="button" onClick={alFallar}>Escribir el código</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden', maxWidth: 520 }}>
        <video ref={video} muted playsInline aria-label="Imagen de la cámara" style={{ width: '100%', display: 'block' }} />
        <div aria-hidden="true" style={{ position: 'absolute', inset: '20% 12%', border: '3px solid rgba(255,255,255,.85)', borderRadius: 8 }} />
      </div>
      <div className="fila" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        {pausada && <button type="button" className="principal" style={{ minHeight: 48, minWidth: 160 }} disabled={ocupado} onClick={leer}>Escanear siguiente</button>}
        {camaras.length > 1 && (
          <button type="button" style={{ minHeight: 48 }} onClick={() => {
            const actual = idCamara ?? stream.current?.getVideoTracks()[0]?.getSettings?.().deviceId;
            const i = camaras.findIndex((c) => c.id === actual);
            setIdCamara(camaras[(i + 1) % camaras.length]!.id);
          }}>Cambiar cámara</button>
        )}
        {conLinterna && (
          <button type="button" style={{ minHeight: 48 }} aria-pressed={luz} onClick={() => { const n = !luz; setLuz(n); void linterna(stream.current, n); }}>
            {luz ? 'Apagar linterna' : 'Linterna'}</button>
        )}
      </div>
      <p className="suave pequena">{pausada ? 'Lectura hecha: pulsa «Escanear siguiente» para leer otro código.' : 'Apunta al código dentro del recuadro.'}</p>
    </div>
  );
}
